import { useState, useEffect } from "react";
import { AlertCircle, CheckCircle, XCircle, Info } from "lucide-react";
import { Button } from "../../primitives/Button";
import { Modal } from "../../primitives/Modal";
import { validateDraft, publishDryRun, type ProcessTemplateValidationResult, type ValidationError, type ProcessTemplatePublishDryRun } from "../../../api/processes";
import { cn } from "../../utils/cn";

type ProcessTemplateValidationPanelProps = {
  templateId: string;
  onValidationChange?: (isValid: boolean) => void;
};

export function ProcessTemplateValidationPanel({
  templateId,
  onValidationChange,
}: ProcessTemplateValidationPanelProps) {
  const [validation, setValidation] = useState<ProcessTemplateValidationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<ProcessTemplatePublishDryRun | null>(null);
  const [isLoadingDryRun, setIsLoadingDryRun] = useState(false);

  const loadValidation = async () => {
    setIsLoading(true);
    try {
      const result = await validateDraft(templateId);
      setValidation(result);
      onValidationChange?.(result.is_valid);
    } catch (error: any) {
      console.error("Failed to validate draft:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadValidation();
  }, [templateId]);

  const handleDryRun = async () => {
    setIsLoadingDryRun(true);
    try {
      const result = await publishDryRun(templateId);
      setDryRunResult(result);
      setDryRunOpen(true);
    } catch (error: any) {
      console.error("Failed to run publish dry run:", error);
      alert(`Erreur lors de l'analyse: ${error.message || error}`);
    } finally {
      setIsLoadingDryRun(false);
    }
  };

  if (!validation) {
    return (
      <div className="border border-border rounded-lg p-4 bg-surface">
        <div className="text-sm text-muted-foreground">Chargement de la validation...</div>
      </div>
    );
  }

  const hasErrors = validation.errors.length > 0;
  const hasWarnings = validation.warnings.length > 0;
  const isValid = validation.is_valid;

  return (
    <div className="border border-border rounded-lg bg-surface">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isValid ? (
            <CheckCircle size={20} className="text-success" />
          ) : (
            <XCircle size={20} className="text-danger" />
          )}
          <span className="font-medium">
            {isValid ? "Validation réussie" : "Erreurs de validation"}
          </span>
          {hasErrors && (
            <span className="text-xs px-2 py-0.5 bg-danger/10 text-danger rounded">
              {validation.errors.length} erreur{validation.errors.length > 1 ? "s" : ""}
            </span>
          )}
          {hasWarnings && (
            <span className="text-xs px-2 py-0.5 bg-warning/10 text-warning rounded">
              {validation.warnings.length} avertissement{validation.warnings.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            label={isLoadingDryRun ? "Analyse..." : "Publish Dry Run"}
            intent="default"
            size="sm"
            onClick={handleDryRun}
            state={isLoadingDryRun ? "loading" : "default"}
          />
          <Button
            label={isLoading ? "Validation..." : "Revalider"}
            intent="default"
            size="sm"
            onClick={loadValidation}
            state={isLoading ? "loading" : "default"}
          />
        </div>
      </div>

      {(hasErrors || hasWarnings) && (
        <div className="p-4 space-y-3">
          {hasErrors && (
            <div>
              <div className="text-sm font-medium text-danger mb-2">Erreurs</div>
              <div className="space-y-1">
                {validation.errors.map((error, idx) => (
                  <ValidationItem key={idx} error={error} severity="error" />
                ))}
              </div>
            </div>
          )}
          {hasWarnings && (
            <div>
              <div className="text-sm font-medium text-warning mb-2">Avertissements</div>
              <div className="space-y-1">
                {validation.warnings.map((warning, idx) => (
                  <ValidationItem key={idx} error={warning} severity="warning" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isValid && !hasWarnings && (
        <div className="p-4 text-sm text-muted-foreground text-center">
          Aucune erreur ou avertissement détecté.
        </div>
      )}

      {/* Publish Dry Run Modal */}
      <Modal
        open={dryRunOpen}
        onOpenChange={setDryRunOpen}
        title="Analyse de publication"
        showDefaultButtons={false}
        showCloseButton={true}
        size="md"
      >
        {dryRunResult && (
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg bg-surface-subtle">
              <div className="flex items-center gap-2 mb-2">
                <Info size={20} className="text-info" />
                <span className="font-medium">Impact de la publication</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>Tâches affectées: <span className="font-medium text-text">{dryRunResult.affected_tasks_count}</span></p>
              </div>
            </div>

            {dryRunResult.breaking_changes && dryRunResult.breaking_changes.length > 0 && (
              <div>
                <div className="text-sm font-medium text-warning mb-2">Changements majeurs détectés</div>
                <div className="space-y-2">
                  {dryRunResult.breaking_changes.map((change, idx) => (
                    <div key={idx} className="p-3 border border-warning/30 rounded bg-warning/10">
                      <div className="text-sm font-medium text-warning">{change.type}</div>
                      <div className="text-sm text-muted-foreground mt-1">{change.description}</div>
                      {change.affected_count > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {change.affected_count} tâche{change.affected_count > 1 ? "s" : ""} affectée{change.affected_count > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dryRunResult.migration_notes && dryRunResult.migration_notes.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">Notes de migration</div>
                <div className="space-y-1">
                  {dryRunResult.migration_notes.map((note, idx) => (
                    <div key={idx} className="text-sm text-muted-foreground p-2 bg-surface-subtle rounded">
                      {note}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dryRunResult.breaking_changes?.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground text-center border border-border rounded">
                Aucun changement majeur détecté. La publication peut se faire en toute sécurité.
              </div>
            )}

            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                label="Fermer"
                intent="default"
                size="sm"
                onClick={() => setDryRunOpen(false)}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function ValidationItem({ error, severity }: { error: ValidationError; severity: "error" | "warning" }) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 p-2 rounded text-sm",
        severity === "error" ? "bg-danger/10 text-danger" : "bg-warning/10 text-warning"
      )}
    >
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        {error.field && <span className="font-medium">{error.field}: </span>}
        <span>{error.message}</span>
      </div>
    </div>
  );
}
